import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import Styles from './CollabItemsFilters.module.scss'
import { useDocStore } from 'stores/DocStore'
import { baseFilters, resolvedFilter } from 'stores/CollabItemsFilters'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { MenuItem } from './MenuItem/MenuItem'
import { Counter, CounterVariant, CounterSize } from 'components/Counter/Counter'
import { values } from 'mobx'
import { ArticleView, UserRole } from 'types'
import { useSearchParams } from 'hooks/useSearchParams'
import { useCanViewAs } from 'components/Can'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { CollabItemType } from 'stores/DocStore.types'
import { Check2, Comment, Loader1, Task, TrackedChanges } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Close } from 'blaze-ui/icons/16'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Button } from 'common/Prose/Toolbar/ToolbarBravo/WriterFormatToolbar/Button'
import { Filter } from 'blaze-ui/icons/24'

interface Props {
  onClose?: () => void
  workspaceId: number
}

export const CollabItemsFilters = observer(({ workspaceId, onClose }: Props) => {
  const canViewAs = useCanViewAs(workspaceId)
  const docStore = useDocStore()
  const articleView = useArticleView()
  const { isMobile } = useResponsiveDesign()
  const userFilters = docStore.filters.usersFilters
  const validBaseFilters = baseFilters.filter(
    (item) =>
      (articleView === ArticleView.Editing || isMobile) &&
      (canViewAs([UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) ||
        item.key !== 'trackedChanges'),
  )
  const filtersCount = values(docStore.filters.activeKeys).filter(
    (item) => !validBaseFilters.map((baseFilter) => baseFilter.key).includes(item),
  ).length
  const defaultFilter = useSearchParams().get('collabItemsFilter')

  const baseFilterIcon = {
    comments: <Comment />,
    todos: <Task />,
    trackedChanges: <TrackedChanges />,
  }

  // Hydrate users for filters
  useEffect(() => {
    userFilters.forEach((userFilter) => {
      docStore.rootStore.users.fetch(userFilter.userId)
    })
    if (defaultFilter) docStore.filters.activeKeys.add(defaultFilter)
  }, [userFilters, docStore, defaultFilter])

  const clearFilters = () => {
    docStore.filters.clear()
  }

  // Deactivate all filters that are active, then activate the selected base filter
  const setBaseFilter = (filter: string) => {
    clearFilters()
    docStore.rightRailStore.openSection(filter)
    docStore.filters.activate(filter)
  }

  const toggleResolvedFilter = () => {
    if (!resolvedFilter) return

    if (docStore.filters.activeKeys.has(resolvedFilter.key)) {
      docStore.filters.deactivate(resolvedFilter.key)
    } else {
      docStore.filters.activate(resolvedFilter.key)
    }
  }

  // if selected user filter is active, deactivate it
  // else, deactivate any active user filters and activate new one
  const toggleUserFilter = (filter: string) => {
    if (docStore.filters.activeKeys.has(filter)) {
      docStore.filters.deactivate(filter)
    } else {
      const activeUserFilters = docStore.filters.active.filter((item) => item.key.includes('user'))
      if (activeUserFilters.length > 0) {
        activeUserFilters.forEach((userFilter) => docStore.filters.deactivate(userFilter.key))
      }

      docStore.filters.activate(filter)
    }
  }

  if (validBaseFilters.length <= 1) return null

  const filters = (
    <>
      <MenuItem
        key="all"
        filter={{
          key: 'all',
          fn: () => true,
          type: CollabItemType.All,
          facet: 'itemType',
          label: 'All',
          isDisabled: () => false,
        }}
        selected={filtersCount === 0}
        onClick={clearFilters}
        icon={<Loader1 />}
        itemsCount={docStore.collabItems.length}
      />
      {!isMobile &&
        validBaseFilters.map((filter) => (
          <MenuItem
            key={filter.key}
            filter={filter}
            onClick={() => setBaseFilter(filter.key)}
            icon={baseFilterIcon[filter.key as 'comments' | 'todos' | 'trackedChanges']}
          />
        ))}
      {userFilters.length > 0 && <div className={Styles.spacer} />}
      {userFilters.map((filter) => (
        <MenuItem
          key={filter.key}
          filter={filter}
          onClick={() => toggleUserFilter(filter.key)}
          user={docStore.rootStore.users.get(filter.userId)}
        />
      ))}
      <div className={Styles.spacer} />
      <MenuItem filter={resolvedFilter} onClick={toggleResolvedFilter} icon={<Check2 />} />
    </>
  )

  if (isMobile)
    return (
      <DropdownTrigger placement="bottom-start" containFocus={false} popoverClassName={Styles.dropdownContainer}>
        <Button aria-label="Filters">
          <div className={Styles.filterButton}>
            <Filter size={16} />
            {filtersCount > 0 && (
              <div className={Styles.triggerCounterContainer}>
                <Counter variant={CounterVariant.Grey} size={CounterSize.xs} count={filtersCount} noBorder />
              </div>
            )}
          </div>
        </Button>
        {() => filters}
      </DropdownTrigger>
    )

  return (
    <div className={Styles.qualifierContainer}>
      <div className={Styles.filters}>
        <div className={Styles.title}>
          <Heading level={5}>Collaboration</Heading>
        </div>
        <div className={Styles.buttons}>
          <DropdownTrigger placement="bottom-start" containFocus={false} popoverClassName={Styles.dropdownContainer}>
            <IconButton size="xs" variant="tertiary" aria-label="Filters" rounded>
              <div className={Styles.filterButton}>
                <Filter size={16} />
                {filtersCount > 0 && (
                  <div className={Styles.triggerCounterContainer}>
                    <Counter variant={CounterVariant.Grey} size={CounterSize.xs} count={filtersCount} noBorder />
                  </div>
                )}
              </div>
            </IconButton>
            {() => filters}
          </DropdownTrigger>
          <IconButton size="xs" variant="tertiary" aria-label="close" rounded icon={Close} onPress={onClose} />
        </div>
      </div>
    </div>
  )
})
